import { authedFetch } from '../auth/supabase-browser';
import { apiUrl } from '../api/client-modules';
import type {
  MessagePageContext,
  MessagePreflightResponse,
  MessageRecordType,
  MessageThreadDetail,
  MessageThreadListResponse,
  MessagesPortalSettings,
  NotificationListResponse,
  NotificationEventTypeName,
  NotificationPreferencesResponse,
  MessageItem,
  MessageThreadSummary,
  MessageAttachmentItem,
} from '@oneup/shared';

/**
 * Client Portal Messages API client. The browser only ever supplies the
 * verified client_id scope plus display data — sender identity, tenant, and
 * visibility are derived server-side.
 */

async function request<T>(path: string, clientId: string, init?: RequestInit): Promise<T> {
  const url = new URL(apiUrl(path), window.location.origin);
  url.searchParams.set('client_id', clientId);
  const response = await authedFetch(url.toString(), {
    headers: { Accept: 'application/json', ...(init?.body ? { 'Content-Type': 'application/json' } : {}) },
    ...init,
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      (body as { error?: { message?: string } } | null)?.error?.message ??
      'We couldn’t complete that just now. Please try again in a moment.';
    throw new Error(message);
  }
  return body as T;
}

export function fetchMessagesSettings(clientId: string): Promise<MessagesPortalSettings> {
  return request('/api/client/messages/settings', clientId);
}

export function fetchThreads(clientId: string): Promise<MessageThreadListResponse> {
  return request('/api/client/messages/threads', clientId);
}

export function fetchThread(clientId: string, threadId: string): Promise<MessageThreadDetail> {
  return request(`/api/client/messages/threads/${encodeURIComponent(threadId)}`, clientId);
}

/** Threads linked to a record, scoped to this client's tenant. */
export function fetchThreadsForRecord(
  clientId: string,
  recordType: MessageRecordType,
  recordId: string,
): Promise<MessageThreadListResponse> {
  return request(
    `/api/client/messages/records/${encodeURIComponent(recordType)}/${encodeURIComponent(recordId)}/threads`,
    clientId,
  );
}

export function createThread(
  clientId: string,
  input: { subject?: string; body: string; context?: MessagePageContext },
): Promise<{ thread: MessageThreadSummary; message: MessageItem }> {
  return request('/api/client/messages/threads', clientId, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function replyToThread(
  clientId: string,
  threadId: string,
  body: string,
  options: { hasAttachments?: boolean } = {},
): Promise<{ message: MessageItem }> {
  return request(`/api/client/messages/threads/${encodeURIComponent(threadId)}/messages`, clientId, {
    method: 'POST',
    body: JSON.stringify({ body, hasAttachments: options.hasAttachments === true }),
  });
}

export function markThreadRead(clientId: string, threadId: string): Promise<{ ok: boolean }> {
  return request(`/api/client/messages/threads/${encodeURIComponent(threadId)}/read`, clientId, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function runPreflight(
  clientId: string,
  input: { body: string; context?: MessagePageContext },
): Promise<MessagePreflightResponse> {
  return request('/api/client/messages/preflight', clientId, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function uploadThreadAttachment(
  clientId: string,
  threadId: string,
  file: File,
  messageId?: string,
): Promise<{ item: MessageAttachmentItem }> {
  const url = new URL(
    apiUrl(`/api/client/messages/threads/${encodeURIComponent(threadId)}/attachments`),
    window.location.origin,
  );
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('filename', file.name);
  if (messageId) url.searchParams.set('messageId', messageId);
  const response = await authedFetch(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'Content-Length': String(file.size),
    },
    body: file,
  });
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      (body as { error?: { message?: string } } | null)?.error?.message ??
      'We couldn’t upload that file just now. Please try again in a moment.';
    throw new Error(message);
  }
  return body as { item: MessageAttachmentItem };
}

export function fetchNotifications(
  clientId: string,
  options: { eventType?: NotificationEventTypeName; limit?: number } = {},
): Promise<NotificationListResponse> {
  const params = new URLSearchParams({ limit: String(options.limit ?? 20) });
  if (options.eventType) params.set('event_type', options.eventType);
  return request(`/api/client/messages/notifications?${params.toString()}`, clientId);
}

export function markNotificationRead(clientId: string, id: string): Promise<{ ok: boolean }> {
  return request(`/api/client/messages/notifications/${encodeURIComponent(id)}/read`, clientId, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function markAllNotificationsRead(clientId: string): Promise<{ ok: boolean }> {
  return request('/api/client/messages/notifications/read-all', clientId, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function fetchNotificationPreferences(clientId: string): Promise<NotificationPreferencesResponse> {
  return request('/api/client/messages/preferences', clientId);
}

export function saveNotificationPreferences(
  clientId: string,
  items: NotificationPreferencesResponse['items'],
  options: { impersonationNotificationEditing?: boolean } = {},
): Promise<{ ok: boolean }> {
  return request('/api/client/messages/preferences', clientId, {
    method: 'PUT',
    ...(options.impersonationNotificationEditing ? {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Impersonation-Edit': 'notification-preferences',
      },
    } : {}),
    body: JSON.stringify({ items }),
  });
}

export function sendHeartbeat(
  clientId: string,
  input: { route?: string; threadId?: string | null; focused?: boolean },
): Promise<{ ok: boolean }> {
  return request('/api/client/messages/heartbeat', clientId, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
